import React, { useState } from 'react';
import { AlertTriangle, Shield, Info, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

const TransferRestrictionBanner = ({ site, editPermissions }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Don't show banner if no transfer restrictions
  if (!site.transferInfo?.restrictedAccess || !editPermissions?.restrictions?.length) {
    return null;
  }

  const restrictions = site.transferInfo.restrictions || [];
  const transferDate = site.transferInfo.transferDate;
  
  return (
    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
      <div className="flex">
        <div className="flex-shrink-0">
          <Shield className="h-5 w-5 text-yellow-400" />
        </div>
        <div className="ml-3 flex-1">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-yellow-800">
                Limited Editing Access
              </h3>
              <div className="mt-1 text-sm text-yellow-700">
                <p>
                  This site was transferred to you on {' '}
                  <span className="font-medium">
                    {new Date(transferDate).toLocaleDateString()}
                  </span>. 
                  Some features are restricted due to your current plan limitations.
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center text-sm text-yellow-700 hover:text-yellow-800 transition-colors"
            >
              {isExpanded ? (
                <>
                  <span className="mr-1">Hide details</span>
                  <ChevronUp className="w-4 h-4" />
                </>
              ) : (
                <>
                  <span className="mr-1">Show details</span>
                  <ChevronDown className="w-4 h-4" />
                </>
              )}
            </button>
          </div>

          {isExpanded && (
            <div className="mt-4">
              <div className="bg-yellow-100 rounded-lg p-3">
                <h4 className="text-sm font-medium text-yellow-800 mb-2 flex items-center">
                  <Info className="w-4 h-4 mr-1" />
                  Current Restrictions
                </h4>
                <ul className="space-y-2 text-sm text-yellow-700">
                  {restrictions.map((restriction, index) => (
                    <li key={index} className="flex items-start">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium capitalize">{restriction.feature}:</span>
                        <span className="ml-1">{restriction.message}</span>
                        {restriction.page && (
                          <span className="text-yellow-600 ml-1">(Page: {restriction.page})</span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <div className="text-sm text-yellow-700">
                  <strong>What you can do:</strong>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>Publish and view your site</li>
                    <li>Edit content within your plan limits</li>
                    <li>Manage basic settings</li>
                  </ul>
                </div>
                <div className="text-sm">
                  <button 
                    onClick={() => {
                      // Navigate to subscription/upgrade page
                      window.location.href = '/dashboard#subscription';
                    }}
                    className="inline-flex items-center px-3 py-1.5 bg-yellow-600 text-yellow-50 text-xs font-medium rounded hover:bg-yellow-700 transition-colors"
                  >
                    <ExternalLink className="w-3 h-3 mr-1" />
                    Upgrade Plan
                  </button>
                </div>
              </div>

              {site.transferInfo.transferMessage && (
                <div className="mt-3 p-2 bg-yellow-100 rounded border-l-2 border-yellow-300">
                  <p className="text-sm font-medium text-yellow-800 mb-1">Transfer Message:</p>
                  <p className="text-sm text-yellow-700 italic">"{site.transferInfo.transferMessage}"</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransferRestrictionBanner;